import {
  getObjectShape,
  getPath,
  getSchemaFieldPaths,
  getShapeField,
  isOmittableSchema,
  toRecord,
  unwrapTransparentSchema,
} from '@main/shared/utils';
import deepmerge from 'deepmerge';
import z from 'zod/v4';

export type ConnectorConfigParts = {
  config: Record<string, unknown>;
  encryptedConfig: Record<string, unknown>;
};

export type PartialConnectorConfigParts = {
  config?: Record<string, unknown>;
  encryptedConfig?: Record<string, unknown>;
};

type JsonObject = Record<string, unknown>;

type EncryptedConfigPatchResult = {
  value: JsonObject;
  emptiedByClear: boolean;
};

export type ConnectorConfigInputErrorKind =
  | 'invalid-object'
  | 'unknown-field'
  | 'schema-validation';

export type ConnectorConfigInputIssue = {
  readonly path: PropertyKey[];
  readonly message: string;
};

type ConnectorConfigInputErrorOptions = ErrorOptions & {
  readonly issues?: readonly ConnectorConfigInputIssue[];
};

export class ConnectorConfigInputError extends Error {
  override readonly name = 'ConnectorConfigInputError';
  readonly issues: readonly ConnectorConfigInputIssue[];

  constructor(
    readonly kind: ConnectorConfigInputErrorKind,
    message: string,
    options?: ConnectorConfigInputErrorOptions,
  ) {
    super(message, options);
    this.issues = options?.issues ?? [];
  }
}

export type ConnectorConfigSchemas = {
  configSchema: z.ZodObject;
  encryptedConfigSchema: z.ZodObject;
};

export type EncryptedConnectorConfigField = {
  path: string[];
  configured: boolean;
};

export type ParsedConnectorConfigParts<TSchema extends ConnectorConfigSchemas> = {
  config: z.infer<TSchema['configSchema']>;
  encryptedConfig: z.infer<TSchema['encryptedConfigSchema']>;
  mergedConfig: z.infer<TSchema['configSchema']> & z.infer<TSchema['encryptedConfigSchema']>;
};

export function buildRedirectAuthCallbackConfig<TAuth extends ConnectorConfigSchemas>(
  auth: TAuth,
  {
    existingConfig,
    pendingUserConfig,
    callbackConfig,
  }: {
    existingConfig?: PartialConnectorConfigParts | null;
    pendingUserConfig?: PartialConnectorConfigParts | null;
    callbackConfig?: PartialConnectorConfigParts | null;
  },
): ParsedConnectorConfigParts<TAuth> {
  const layers = [existingConfig, pendingUserConfig, callbackConfig];

  return parseConnectorConfigParts(auth, {
    config: mergeRedirectAuthConfigPartition(layers.map((layer) => layer?.config)),
    encryptedConfig: mergeRedirectAuthConfigPartition(
      layers.map((layer) => layer?.encryptedConfig),
    ),
  });
}

export function buildRedirectAuthCallbackConfigFromUserInput<
  TAuth extends ConnectorConfigSchemas & { userConfigSchema: ConnectorConfigSchemas },
>(
  auth: TAuth,
  {
    existingConfig,
    pendingUserConfig,
    callbackConfig,
  }: {
    existingConfig?: PartialConnectorConfigParts | null;
    pendingUserConfig?: PartialConnectorConfigParts | null;
    callbackConfig?: PartialConnectorConfigParts | null;
  },
): ParsedConnectorConfigParts<TAuth> {
  if (existingConfig === null || existingConfig === undefined) {
    return buildRedirectAuthCallbackConfig(auth, {
      pendingUserConfig: parseConnectorConfigParts(auth.userConfigSchema, {
        config: pendingUserConfig?.config,
        encryptedConfig: pendingUserConfig?.encryptedConfig,
      }),
      callbackConfig,
    });
  }

  const pendingConfig = parseConnectorPlainConfig(
    auth.userConfigSchema.configSchema,
    pendingUserConfig?.config,
  );
  const existingEncryptedConfig = existingConfig?.encryptedConfig ?? {};
  const encryptedConfigPatch = pendingUserConfig?.encryptedConfig ?? {};

  parseConnectorUserEncryptedConfigPatch(auth.userConfigSchema, {
    existingEncryptedConfig,
    encryptedConfigPatch,
  });

  const patchedEncryptedConfig = applyEncryptedConfigPatch(
    existingEncryptedConfig,
    encryptedConfigPatch,
    auth.userConfigSchema.encryptedConfigSchema,
  );

  return parseConnectorConfigParts(auth, {
    config: mergeRedirectAuthConfigPartition([
      existingConfig?.config,
      pendingConfig,
      callbackConfig?.config,
    ]),
    encryptedConfig: mergeRedirectAuthConfigPartition([
      patchedEncryptedConfig,
      callbackConfig?.encryptedConfig,
    ]),
  });
}

function mergeRedirectAuthConfigPartition(
  layers: Array<Record<string, unknown> | undefined>,
): Record<string, unknown> {
  return deepmerge.all<Record<string, unknown>>(
    layers.map((layer) => layer ?? {}),
    {
      isMergeableObject: isPlainObject,
      arrayMerge: (_target, source: unknown[]) => source,
    },
  );
}

export function parseConnectorConfigParts<TAuth extends ConnectorConfigSchemas>(
  auth: TAuth,
  input: {
    config: unknown;
    encryptedConfig: unknown;
  },
): ParsedConnectorConfigParts<TAuth> {
  const { config, encryptedConfig } = parseConnectorConfigInput(() =>
    getConnectorConfigPartsSchema(auth).parse({
      config: input.config ?? {},
      encryptedConfig: input.encryptedConfig ?? {},
    }),
  );

  return {
    config,
    encryptedConfig,
    mergedConfig: mergeConnectorConfig(config, encryptedConfig),
  };
}

function parseConnectorConfigInput<T>(parse: () => T): T {
  try {
    return parse();
  } catch (error) {
    if (error instanceof ConnectorConfigInputError) {
      throw error;
    }
    if (error instanceof z.ZodError) {
      throw new ConnectorConfigInputError('schema-validation', 'Connector config is invalid', {
        cause: error,
        issues: error.issues.map((issue) => ({
          path: [...issue.path],
          message: issue.message,
        })),
      });
    }
    throw error;
  }
}

export function parseConnectorPlainConfigDraft<TConfigSchema extends z.ZodObject>(
  configSchema: TConfigSchema,
  input: unknown,
): Partial<z.infer<TConfigSchema>> {
  return getConnectorPlainConfigDraftSchema(configSchema).parse(input ?? {}) as Partial<
    z.infer<TConfigSchema>
  >;
}

export function parseConnectorPlainConfig<TConfigSchema extends z.ZodObject>(
  configSchema: TConfigSchema,
  input: unknown,
): z.infer<TConfigSchema> {
  return parseConnectorConfigInput(() => configSchema.parse(input ?? {}));
}

function getConnectorPlainConfigDraftSchema(configSchema: z.ZodObject): z.ZodObject {
  return makePlainConfigDraftSchema(configSchema);
}

export function parseConnectorEncryptedConfigPatch<TAuth extends ConnectorConfigSchemas>(
  auth: TAuth,
  input: {
    existingEncryptedConfig: unknown;
    encryptedConfigPatch: unknown;
  },
) {
  return parseConnectorConfigInput(() => {
    const existingEncryptedConfig = parseJsonObject(input.existingEncryptedConfig ?? {});
    const encryptedConfigPatch = parseEncryptedConfigPatch(input.encryptedConfigPatch ?? {});
    const patched = applyEncryptedConfigPatch(
      existingEncryptedConfig,
      encryptedConfigPatch,
      auth.encryptedConfigSchema,
    );

    return auth.encryptedConfigSchema.parse(patched);
  });
}

export function parseConnectorUserEncryptedConfigPatch<TSchemas extends ConnectorConfigSchemas>(
  schemas: TSchemas,
  input: {
    existingEncryptedConfig: unknown;
    encryptedConfigPatch: unknown;
  },
) {
  const existingEncryptedConfig = parseJsonObject(input.existingEncryptedConfig ?? {});

  return parseConnectorEncryptedConfigPatch(schemas, {
    existingEncryptedConfig: projectJsonObject(
      existingEncryptedConfig,
      schemas.encryptedConfigSchema,
    ),
    encryptedConfigPatch: input.encryptedConfigPatch,
  });
}

function applyEncryptedConfigPatch(
  existing: JsonObject,
  patch: JsonObject,
  schema: z.ZodObject,
): JsonObject {
  return applyEncryptedConfigPatchState(existing, patch, schema).value;
}

function applyEncryptedConfigPatchState(
  existing: JsonObject,
  patch: JsonObject,
  schema: z.ZodObject,
): EncryptedConfigPatchResult {
  const result: JsonObject = { ...existing };
  const schemaShape = getObjectShape(schema);
  let emptiedByClear = false;

  for (const [key, patchValue] of Object.entries(patch)) {
    if (!Object.prototype.hasOwnProperty.call(schemaShape, key)) {
      throw new ConnectorConfigInputError('unknown-field', `Unknown schema shape key "${key}"`);
    }
    const keySchema = getShapeField(schemaShape, key);
    if (patchValue === null) {
      delete result[key];
      emptiedByClear = true;
      continue;
    }

    const unwrappedSchema = unwrapTransparentSchema(keySchema);
    if (isPlainObject(patchValue) && unwrappedSchema instanceof z.ZodObject) {
      const existingValue = isPlainObject(result[key]) ? result[key] : {};
      const nested = applyEncryptedConfigPatchState(existingValue, patchValue, unwrappedSchema);
      if (
        nested.emptiedByClear &&
        Object.keys(nested.value).length === 0 &&
        isOmittableSchema(keySchema)
      ) {
        delete result[key];
        emptiedByClear = true;
      } else {
        result[key] = nested.value;
      }
      continue;
    }

    result[key] = patchValue;
  }

  return { value: result, emptiedByClear };
}

function parseJsonObject(value: unknown): JsonObject {
  if (!isPlainObject(value)) {
    throw new Error('Connector config must be a JSON object');
  }
  return value;
}

function parseEncryptedConfigPatch(value: unknown): JsonObject {
  if (!isPlainObject(value)) {
    throw new ConnectorConfigInputError(
      'invalid-object',
      'Connector encrypted config patch must be a JSON object',
    );
  }
  return value;
}

function isPlainObject(value: unknown): value is JsonObject {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function projectJsonObject(value: JsonObject, schema: z.ZodObject): JsonObject {
  const result: JsonObject = {};

  for (const [key, fieldSchema] of Object.entries(getObjectShape(schema))) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      continue;
    }

    const fieldValue = value[key];
    const unwrappedSchema = unwrapTransparentSchema(fieldSchema);
    result[key] =
      isPlainObject(fieldValue) && unwrappedSchema instanceof z.ZodObject
        ? projectJsonObject(fieldValue, unwrappedSchema)
        : fieldValue;
  }

  return result;
}

export function getConnectorConfigPartsSchema<T extends ConnectorConfigSchemas>(schemas: T) {
  return z.object({
    config: schemas.configSchema,
    encryptedConfig: schemas.encryptedConfigSchema,
  }) as unknown as z.ZodType<{
    config: z.infer<T['configSchema']>;
    encryptedConfig: z.infer<T['encryptedConfigSchema']>;
  }>;
}

export function mergeConnectorConfig<
  TConfig extends Record<string, unknown>,
  TEncryptedConfig extends Record<string, unknown>,
>(config: TConfig, encryptedConfig: TEncryptedConfig): TConfig & TEncryptedConfig;
export function mergeConnectorConfig(
  config: unknown,
  encryptedConfig: unknown,
): Record<string, unknown>;
export function mergeConnectorConfig(
  config: unknown,
  encryptedConfig: unknown,
): Record<string, unknown> {
  return {
    ...toRecord(config),
    ...toRecord(encryptedConfig),
  };
}

function makePlainConfigDraftSchema(schema: z.ZodObject): z.ZodObject {
  return schema
    .partial()
    .extend(
      Object.fromEntries(
        Object.entries(getObjectShape(schema)).map(([key, fieldSchema]) => [
          key,
          makePlainConfigDraftFieldSchema(fieldSchema),
        ]),
      ),
    );
}

function makePlainConfigDraftFieldSchema(fieldSchema: z.ZodType): z.ZodType {
  const unwrappedSchema = unwrapTransparentSchema(fieldSchema);
  if (unwrappedSchema instanceof z.ZodObject) {
    const draftSchema = makePlainConfigDraftSchema(unwrappedSchema);
    return fieldSchema.safeParse(null).success
      ? draftSchema.nullable().optional()
      : draftSchema.optional();
  }

  return fieldSchema.optional();
}

export function getEncryptedConnectorConfigFields(
  auth: Pick<ConnectorConfigSchemas, 'encryptedConfigSchema'>,
  connectorConfigParts: Pick<ConnectorConfigParts, 'encryptedConfig'>,
): EncryptedConnectorConfigField[] {
  return getSchemaFieldPaths(auth.encryptedConfigSchema).map((path) => {
    const value = getPath(connectorConfigParts.encryptedConfig, path);

    return {
      path,
      configured: value !== undefined && value !== null,
    };
  });
}
